import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { GateScreen, useSubscriberGate } from "@/components/SubscriberGate";
import { Info, TrendingUp, Trophy, ChevronDown, ChevronRight, Flame, Star, Award } from "lucide-react";

const PROVINCES = ["", "ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const DIVS = ["", "18U", "17U", "16U", "15U", "14U", "13U"];
const MIN_GAMES = [["0", "Any GP"], ["3", "3+ GP"], ["5", "5+ GP"], ["10", "10+ GP"]];

const fmtRating = (r) => (r == null ? "—" : Number(r).toFixed(2));
const rk = (v) => (v == null ? "—" : `#${v}`);
const gradeColor = (g) => {
  if (!g) return "bg-slate-100 text-slate-400";
  const l = g[0];
  return l === "A" ? "bg-emerald-500 text-white" : l === "B" ? "bg-sky-500 text-white"
    : l === "C" ? "bg-amber-500 text-white" : l === "D" ? "bg-orange-500 text-white" : "bg-[#E63946] text-white";
};
const GradePill = ({ grade, testid }) => (
  <span data-testid={testid} className={`inline-block font-display text-sm px-1.5 py-0.5 rounded-sm leading-none ${gradeColor(grade)}`}>{grade || "—"}</span>
);

function TrendCell({ rankChange, trend }) {
  if (rankChange != null && rankChange !== 0) {
    const up = rankChange > 0;
    return (
      <span data-testid="trend-cell" className={`inline-flex items-center gap-0.5 font-semibold text-xs ${up ? "text-emerald-600" : "text-[#E63946]"}`}>
        {up ? "▲" : "▼"} {Math.abs(rankChange)}
      </span>
    );
  }
  if (trend === "up") return <span data-testid="trend-cell" className="text-emerald-600 text-xs font-semibold">▲</span>;
  if (trend === "down") return <span data-testid="trend-cell" className="text-[#E63946] text-xs font-semibold">▼</span>;
  return <span data-testid="trend-cell" className="text-slate-300 text-xs">—</span>;
}

function Last5({ items }) {
  if (!items?.length) return <span className="text-xs text-slate-400">No games yet.</span>;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="detail-last5">
      {items.map((g, i) => (
        <span key={i} title={`${g.outcome} ${g.us}-${g.them} vs ${g.opponent}`}
          className={`inline-flex items-center justify-center min-w-[52px] px-1.5 py-1 rounded-sm text-[11px] font-semibold ${g.outcome === "W" ? "bg-emerald-50 text-emerald-700" : g.outcome === "L" ? "bg-red-50 text-[#E63946]" : "bg-slate-100 text-slate-500"}`}>
          {g.outcome} {g.us}-{g.them}{g.quality_win ? " ★" : ""}
        </span>
      ))}
    </div>
  );
}

function RowDetail({ detail }) {
  if (!detail) return <div className="py-4 text-center text-xs text-slate-400">Loading detail…</div>;
  const streak = detail.current_streak || 0;
  const streakLabel = streak > 0 ? `W${streak}` : streak < 0 ? `L${Math.abs(streak)}` : "—";
  return (
    <div className="grid sm:grid-cols-2 gap-4 p-4 bg-slate-50 border-t border-slate-100" data-testid="ranking-row-detail">
      <div className="space-y-3">
        <div>
          <div className="label-overline text-[10px] mb-1.5">Last 5 Games</div>
          <Last5 items={detail.last_5} />
        </div>
        <div className="flex items-center gap-6">
          <div>
            <div className="label-overline text-[10px]">Current Streak</div>
            <div className="font-data text-lg text-[#0A192F] flex items-center gap-1">
              {streak > 0 && <Flame className="w-4 h-4 text-[#E63946]" />}{streakLabel}
            </div>
          </div>
          <div>
            <div className="label-overline text-[10px]">Avg Opp Rating (SoS)</div>
            <div className="font-data text-lg text-[#0A192F]">{fmtRating(detail.strength_of_schedule)}</div>
          </div>
          <div>
            <div className="label-overline text-[10px]">Quality Wins</div>
            <div className="font-data text-lg text-[#0A192F]">{detail.quality_win_count || 0}</div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {detail.top_ranked_win && (
          <div>
            <div className="label-overline text-[10px] mb-1 flex items-center gap-1"><Star className="w-3 h-3 text-[#E63946]" /> Top Ranked Win</div>
            <div className="text-sm">
              {detail.top_ranked_win.opponent_slug
                ? <Link to={`/live/teams/${detail.top_ranked_win.opponent_slug}`} className="font-semibold text-[#0A192F] hover:text-[#E63946]">{detail.top_ranked_win.opponent}</Link>
                : <span className="font-semibold">{detail.top_ranked_win.opponent}</span>}
              <span className="text-slate-500"> · {detail.top_ranked_win.score} · Rated {fmtRating(detail.top_ranked_win.opponent_rating)}</span>
            </div>
          </div>
        )}
        {detail.quality_wins?.length > 0 && (
          <div>
            <div className="label-overline text-[10px] mb-1 flex items-center gap-1"><Award className="w-3 h-3 text-[#E63946]" /> Quality Wins</div>
            <div className="space-y-1">
              {detail.quality_wins.slice(0, 4).map((q, i) => (
                <div key={i} className="text-xs text-slate-600">
                  {q.opponent_slug ? <Link to={`/live/teams/${q.opponent_slug}`} className="font-semibold hover:text-[#E63946]">{q.opponent}</Link> : <span className="font-semibold">{q.opponent}</span>}
                  <span className="text-slate-400"> {q.score}</span>
                  {q.reasons?.[0] && <span className="text-emerald-600"> · {q.reasons[0].label}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TeamRankings() {
  const [teams, setTeams] = useState([]);
  const [f, setF] = useState({ province: "", age_division: "18U", league_id: "", min_games: "0", only_ranked: false, only_verified: false });
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const { loading: gateLoading, blocked, message } = useSubscriberGate();

  useEffect(() => { api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (gateLoading || blocked) { setLoading(false); return; }
    setLoading(true); setExpanded(null);
    const q = new URLSearchParams();
    if (f.province) q.set("province", f.province);
    if (f.age_division) q.set("age_division", f.age_division);
    if (f.league_id) q.set("league_id", f.league_id);
    if (f.min_games !== "0") q.set("min_games", f.min_games);
    if (f.only_ranked) q.set("only_ranked", "true");
    if (f.only_verified) q.set("only_verified", "true");
    api.get(`/live/team-rankings?${q}`).then(({ data }) => setTeams(data.teams)).catch(() => setTeams([])).finally(() => setLoading(false));
  }, [f, gateLoading, blocked]);

  const toggle = useCallback((t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id);
    if (!details[t.id]) {
      api.get(`/live/teams/${t.id}/ranking-detail`).then(({ data }) => setDetails((d) => ({ ...d, [t.id]: data }))).catch(() => {});
    }
  }, [expanded, details]);

  const sel = "text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white";

  return (
    <div className="min-h-screen bg-[#F1F3F6] overflow-x-hidden">
      <Header />
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><Trophy className="w-4 h-4" /> Canadian Baseball Rankings</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-2">CBR Team Rankings™</h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">Fair, objective rankings powered by the proprietary CBR Team Rating™. Expand any team to see how they earned their spot.</p>
          <Link to="/live/team-rankings/how-it-works" data-testid="how-it-works-cta" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold bg-white/10 hover:bg-white/20 px-4 py-2 rounded-sm transition-colors"><Info className="w-4 h-4" /> How CBR Team Rankings Work</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="team-rankings-page">
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <select data-testid="rankings-province" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className={sel}>
            {PROVINCES.map((p) => <option key={p} value={p}>{p || "All Provinces"}</option>)}
          </select>
          <select data-testid="rankings-division" value={f.age_division} onChange={(e) => setF({ ...f, age_division: e.target.value })} className={sel}>
            {DIVS.map((d) => <option key={d} value={d}>{d || "All Divisions"}</option>)}
          </select>
          <select data-testid="rankings-league" value={f.league_id} onChange={(e) => setF({ ...f, league_id: e.target.value })} className={sel}>
            <option value="">All Leagues</option>
            {leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation || l.name}</option>)}
          </select>
          <select data-testid="rankings-min-games" value={f.min_games} onChange={(e) => setF({ ...f, min_games: e.target.value })} className={sel}>
            {MIN_GAMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="rankings-only-ranked" type="checkbox" checked={f.only_ranked} onChange={(e) => setF({ ...f, only_ranked: e.target.checked })} /> Ranked only</label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="rankings-only-verified" type="checkbox" checked={f.only_verified} onChange={(e) => setF({ ...f, only_verified: e.target.checked })} /> Verified only</label>
        </div>

        {gateLoading ? <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
          : blocked ? <GateScreen message={message} />
          : loading ? <div className="py-16 text-center text-sm text-slate-400">Loading rankings…</div>
          : teams.length === 0 ? <div className="py-16 text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-sm">No teams match these filters yet.</div>
          : <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="grid grid-cols-[44px_1fr_80px_46px_46px_54px_72px_44px_46px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                <span>Rank</span><span>Team</span><span className="text-center">Record</span>
                <span className="text-center">RF</span><span className="text-center">RA</span><span className="text-center">Diff</span>
                <span className="text-right">Rating</span><span className="text-center">Trend</span><span className="text-center">Grade</span>
              </div>
              {teams.map((t) => (
                <div key={t.id} className="border-b border-slate-100 last:border-0">
                  <div data-testid={`ranking-row-${t.slug}`} className="grid grid-cols-[44px_1fr_80px_46px_46px_54px_72px_44px_46px] gap-2 px-4 py-3 items-center hover:bg-slate-50 cursor-pointer" onClick={() => toggle(t)}>
                    <span className="font-data text-lg text-slate-400">{t.rank ? `#${t.rank}` : "—"}</span>
                    <div className="flex items-center gap-3 min-w-0">
                      <button data-testid={`ranking-expand-${t.slug}`} onClick={(e) => { e.stopPropagation(); toggle(t); }} className="text-slate-300 hover:text-[#E63946] shrink-0">
                        {expanded === t.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <Link to={`/live/teams/${t.slug}`} onClick={(e) => e.stopPropagation()} className="shrink-0"><EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-9 h-9" textSize="text-[10px]" /></Link>
                      <div className="min-w-0">
                        <Link to={`/live/teams/${t.slug}`} data-testid={`ranking-team-link-${t.slug}`} onClick={(e) => e.stopPropagation()} className="text-sm font-semibold truncate block hover:text-[#E63946]">{t.name}</Link>
                        <div className="text-[11px] text-slate-400 truncate">{[t.organization_name, t.age_division].filter(Boolean).join(" · ")}</div>
                      </div>
                    </div>
                    <span className="text-center font-data text-sm text-slate-700">{t.record.w}-{t.record.l}{t.record.t ? `-${t.record.t}` : ""}</span>
                    <span className="text-center font-data text-sm text-slate-500">{t.runs_for}</span>
                    <span className="text-center font-data text-sm text-slate-500">{t.runs_against}</span>
                    <span className={`text-center font-data text-sm ${t.run_diff > 0 ? "text-emerald-600" : t.run_diff < 0 ? "text-[#E63946]" : "text-slate-500"}`}>{t.run_diff > 0 ? "+" : ""}{t.run_diff}</span>
                    <span className="text-right font-data text-lg text-[#0A192F]">{fmtRating(t.cbr_team_rating)}</span>
                    <span className="text-center"><TrendCell rankChange={t.rank_change} trend={t.trend} /></span>
                    <span className="text-center"><GradePill grade={t.overall_grade} testid={`ranking-grade-${t.slug}`} /></span>
                  </div>
                  {expanded === t.id && <RowDetail detail={details[t.id]} />}
                </div>
              ))}
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {teams.map((t) => (
                <div key={t.id} data-testid={`ranking-card-${t.slug}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="flex items-center gap-3 p-3" onClick={() => toggle(t)}>
                    <span className="font-data text-lg text-slate-400 w-7 text-right shrink-0">{t.rank ? `#${t.rank}` : "—"}</span>
                    <Link to={`/live/teams/${t.slug}`} onClick={(e) => e.stopPropagation()}><EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-10 h-10" textSize="text-[10px]" /></Link>
                    <div className="min-w-0 flex-1">
                      <Link to={`/live/teams/${t.slug}`} onClick={(e) => e.stopPropagation()} className="text-sm font-semibold truncate block hover:text-[#E63946]">{t.name}</Link>
                      <div className="text-[11px] text-slate-400 truncate">{t.record.w}-{t.record.l}{t.record.t ? `-${t.record.t}` : ""} · {t.run_diff > 0 ? "+" : ""}{t.run_diff} diff</div>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <GradePill grade={t.overall_grade} testid={`ranking-card-grade-${t.slug}`} />
                        <div className="font-data text-lg text-[#0A192F]">{fmtRating(t.cbr_team_rating)}</div>
                      </div>
                      <TrendCell rankChange={t.rank_change} trend={t.trend} />
                    </div>
                    <button className="text-slate-300 shrink-0" data-testid={`ranking-card-expand-${t.slug}`} onClick={(e) => { e.stopPropagation(); toggle(t); }}>
                      {expanded === t.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                  {expanded === t.id && <RowDetail detail={details[t.id]} />}
                </div>
              ))}
            </div>
          </>}
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Ratings populate automatically once teams play games (CBR Live). Early-season ratings show lower confidence.</p>
      </div>
    </div>
  );
}
